import React, { useState } from 'react';
import { Card, Typography, Steps, Button, Input, message, Space, Alert } from 'antd';
import { CopyOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { getActiveBrandId } from '../config/api';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

const BrandIntegration: React.FC = () => {
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  
  // Get active brand ID based on environment (staging uses stageBrandId, prod uses brandId)
  const activeBrandId = getActiveBrandId(user);
  
  const apiKey = activeBrandId || 'fc_your_api_key_here';
  const brandId = activeBrandId || '6233f394-9e10-4cc0-889e-c5fd08263d88';

  const integrationCode = `<!-- Fashion Connect SDK -->
<script src="https://d2iyj97gfddxma.cloudfront.net/fashion-connect-sdk.js"></script>
<script>
  window.FashionConnect.init({
    brandId: '${brandId}',
    brandName: '${user?.name || 'Your Brand'}'
  }).then(() => {
    console.log('✅ Fashion Connect SDK initialized!');
  });
</script>`;

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    message.success('Kopyalandı!');
  };

  const steps = [
    {
      title: 'API Key',
      content: (
        <Card>
          <Title level={4}>1. API Key'inizi Alın</Title>
          <Paragraph>
            Fashion Connect entegrasyonu için aşağıdaki API key'inizi kullanın:
          </Paragraph>
          <Input.Group compact style={{ marginBottom: 16 }}>
            <Input
              style={{ width: 'calc(100% - 100px)' }}
              value={apiKey}
              readOnly
            />
            <Button
              type="primary"
              icon={<CopyOutlined />}
              onClick={() => handleCopy(apiKey)}
            >
              Kopyala
            </Button>
          </Input.Group>
          <Alert
            message="Güvenlik Uyarısı"
            description="API key'inizi güvenli tutun ve başkalarıyla paylaşmayın."
            type="warning"
            showIcon
          />
        </Card>
      ),
    },
    {
      title: 'SDK Entegrasyonu',
      content: (
        <Card>
          <Title level={4}>2. SDK'yı Web Sitenize Ekleyin</Title>
          <Paragraph>
            Aşağıdaki kodu web sitenizin <Text code>&lt;head&gt;</Text> veya{' '}
            <Text code>&lt;body&gt;</Text> bölümüne ekleyin:
          </Paragraph>
          <TextArea
            value={integrationCode}
            rows={12}
            readOnly
            style={{ fontFamily: 'monospace', marginBottom: 16 }}
          />
          <Button
            type="primary"
            icon={<CopyOutlined />}
            onClick={() => handleCopy(integrationCode)}
            block
          >
            Kodu Kopyala
          </Button>
        </Card>
      ),
    },
    {
      title: 'Test',
      content: (
        <Card>
          <Title level={4}>3. Entegrasyonu Test Edin</Title>
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Alert
              message="Test Adımları"
              description={
                <ol style={{ marginTop: 8, paddingLeft: 20 }}>
                  <li>SDK kodunu web sitenize ekleyin</li>
                  <li>Sayfayı yenileyin</li>
                  <li>Browser console'da "Fashion Connect SDK initialized" mesajını görmelisiniz</li>
                  <li>Widget'ın açıldığını test edin</li>
                </ol>
              }
              type="info"
              showIcon
            />
            
            <Card size="small" title="Test Komutu">
              <Paragraph>
                Browser console'da aşağıdaki komutu çalıştırarak widget'ı test edebilirsiniz:
              </Paragraph>
              <Input.Group compact>
                <Input
                  style={{ width: 'calc(100% - 100px)' }}
                  value="window.FashionConnect.open()"
                  readOnly
                />
                <Button
                  type="primary"
                  icon={<CopyOutlined />}
                  onClick={() => handleCopy('window.FashionConnect.open()')}
                >
                  Kopyala
                </Button>
              </Input.Group>
            </Card>

            <Alert
              message="Başarılı! ✅"
              description="Widget açılıyorsa entegrasyon başarıyla tamamlanmıştır."
              type="success"
              showIcon
              icon={<CheckCircleOutlined />}
            />
          </Space>
        </Card>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Title level={2}>Brand Entegrasyonu</Title>
        <Text type="secondary">
          Fashion Connect SDK'yı web sitenize entegre edin
        </Text>
      </div>

      <Steps current={currentStep} style={{ marginBottom: 32 }}>
        {steps.map((item) => (
          <Steps.Step key={item.title} title={item.title} />
        ))}
      </Steps>

      <div style={{ marginBottom: 24 }}>{steps[currentStep].content}</div>

      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button
          disabled={currentStep === 0}
          onClick={() => setCurrentStep(currentStep - 1)}
        >
          Geri
        </Button>
        {currentStep < steps.length - 1 && (
          <Button type="primary" onClick={() => setCurrentStep(currentStep + 1)}>
            İleri
          </Button>
        )}
        {currentStep === steps.length - 1 && (
          <Button type="primary" onClick={() => message.success('Entegrasyon tamamlandı!')}>
            Tamamla
          </Button>
        )}
      </div>
    </div>
  );
};

export default BrandIntegration;

